import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import ActivityApi from '../api/instance/ActivityApi';
import HostApi from '../api/host/HostApi';
import StatisticApi from '../api/instance/StatisticApi';
import TagApi from '../api/TagApi';
import { buildApiQuery, getConvertedTimeToUTC, resolveUserTimezone } from '../helpers/timeRangeUtils';
import { INTERVAL_MINUTE } from '../component/Constants';
import { AlertRuleFormData } from '../types/Alerts/AlertRuleFormState';
import HostTarget from '../types/host/HostTarget';
import InstanceTarget from '../types/instance/InstanceTarget';
import Tags from '../types/Tags';
import {
    AlertRulePreviewEntity,
    getAlertRuleTriggeredCounts,
    getAlertRulePreviewEntities
} from '../helpers/AlertRulePreviewUtils';

dayjs.extend(utc);
dayjs.extend(timezone);

export type AlertRulePreviewEntityType = 'host' | 'datasource';

export interface AlertRulePreviewPoint {
    x: number;
    y: number;
}

export interface AlertRulePreviewState {
    entityType: AlertRulePreviewEntityType;
    matchedEntities: AlertRulePreviewEntity[];
    selectedEntityId?: number;
    selectedEntity?: AlertRulePreviewEntity;
    setSelectedEntityId: (id: number) => void;
    series: AlertRulePreviewPoint[];
    loading: boolean;
    error: string | null;
    emptyReason: string | null;
    previewFrom: dayjs.Dayjs;
    previewTo: dayjs.Dayjs;
}

const getLastPointValue = (points: AlertRulePreviewPoint[]): number | undefined => {
    const lastPoint = [...points].reverse().find(point => point.y !== null && point.y !== undefined && !Number.isNaN(point.y));
    return lastPoint?.y;
};

const getMaxPointValue = (points: AlertRulePreviewPoint[]): number | undefined => {
    const values = points
        .map(point => point.y)
        .filter(value => value !== null && value !== undefined && !Number.isNaN(value));

    return values.length > 0 ? Math.max(...values) : undefined;
};

const getValueForRow = (row: any, aggregation: string, metricType: string): number => {
    if (metricType === 'activity') {
        return Number(row.waittime ?? row.duration ?? row.executions ?? 0);
    }

    return Number(row[aggregation] ?? row.sum ?? row.avg ?? row.value ?? 0);
};

const buildPreviewTimeRange = (intervalMinutes: number) => {
    const timezoneValue = resolveUserTimezone();
    const to = dayjs.tz(undefined, timezoneValue);
    const from = to.subtract(24, 'hour');
    const interval = Math.max(1, intervalMinutes || 1) * INTERVAL_MINUTE;

    return {
        timeRange: {
            from,
            to,
            previousFrom: from.subtract(24, 'hour'),
            timezone: timezoneValue,
            interval
        },
        from,
        to
    };
};

export const useAlertRulePreview = (
    formData: AlertRuleFormData,
    instances: InstanceTarget[],
    hosts: HostTarget[]
): AlertRulePreviewState => {
    const [tags, setTags] = useState<Tags[]>([]);
    const [selectedEntityId, setSelectedEntityId] = useState<number | undefined>();
    const [seriesByEntityId, setSeriesByEntityId] = useState<Record<number, AlertRulePreviewPoint[]>>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (formData.entitytype !== 'datasource') {
            setTags([]);
            return;
        }

        let cancelled = false;
        void TagApi.getAllRawTags().then(result => {
            if (!cancelled) {
                setTags(result);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [formData.entitytype]);

    const matchedEntities = useMemo(
        () => getAlertRulePreviewEntities(formData, instances, hosts, tags),
        [
            formData.entitytype,
            formData.host,
            formData.datasource,
            formData.tag,
            instances,
            hosts,
            tags
        ]
    );

    useEffect(() => {
        if (matchedEntities.length === 0) {
            setSelectedEntityId(undefined);
            return;
        }

        if (!selectedEntityId || !matchedEntities.some(entity => entity.id === selectedEntityId)) {
            setSelectedEntityId(matchedEntities[0].id);
        }
    }, [matchedEntities, selectedEntityId]);

    const { timeRange, from, to } = useMemo(
        () => buildPreviewTimeRange(formData.evaluationwindowminutes),
        [formData.evaluationwindowminutes]
    );

    useEffect(() => {
        if (!formData.statistic || matchedEntities.length === 0) {
            setSeriesByEntityId({});
            setError(null);
            setLoading(false);
            return;
        }

        let cancelled = false;
        const debounce = window.setTimeout(() => {
            const loadPreview = async () => {
                setLoading(true);
                setError(null);

                try {
                    const query = buildApiQuery(timeRange);
                    const responses = await Promise.allSettled(matchedEntities.map(async entity => {
                        let response;

                        if (formData.entitytype === 'host') {
                            response = await HostApi.getHostStatisticTime(query, entity.id, formData.statistic);
                        } else if (formData.metrictype === 'activity') {
                            const activityResponse = await ActivityApi.getTime(query, entity.id, `&statistic=${encodeURIComponent(formData.statistic)}`);
                            response = activityResponse.data;
                        } else {
                            const statisticResponse = await StatisticApi.getTime(query, entity.id, formData.statistic);
                            response = statisticResponse.data;
                        }

                        const points = (response ?? [])
                            .filter((row: any) => row?.timeslice)
                            .map((row: any) => ({
                                x: getConvertedTimeToUTC(row),
                                y: getValueForRow(row, formData.aggregation, formData.metrictype)
                            }));

                        return { entityId: entity.id, points };
                    }));

                    if (cancelled) return;

                    const nextSeriesByEntityId: Record<number, AlertRulePreviewPoint[]> = {};
                    let failedCount = 0;

                    responses.forEach(result => {
                        if (result.status === 'fulfilled') {
                            nextSeriesByEntityId[result.value.entityId] = result.value.points;
                        } else {
                            failedCount += 1;
                            console.error('Failed to load alert rule preview entity.', result.reason);
                        }
                    });

                    setSeriesByEntityId(nextSeriesByEntityId);
                    setError(failedCount > 0 ? 'Some preview data could not be loaded.' : null);
                } catch (previewError) {
                    if (!cancelled) {
                        console.error('Failed to load alert rule preview.', previewError);
                        setSeriesByEntityId({});
                        setError('Preview data could not be loaded.');
                    }
                } finally {
                    if (!cancelled) {
                        setLoading(false);
                    }
                }
            };

            void loadPreview();
        }, 350);

        return () => {
            cancelled = true;
            window.clearTimeout(debounce);
        };
    }, [
        formData.entitytype,
        formData.metrictype,
        formData.statistic,
        formData.aggregation,
        matchedEntities,
        timeRange
    ]);

    const matchedEntitiesWithValues = useMemo(() => {
        return matchedEntities.map(entity => ({
            ...entity,
            lastValue: getLastPointValue(seriesByEntityId[entity.id] ?? []),
            maxValue: getMaxPointValue(seriesByEntityId[entity.id] ?? []),
            severityCounts: getAlertRuleTriggeredCounts(
                (seriesByEntityId[entity.id] ?? []).map(point => point.y),
                formData.thresholdarray,
                formData.decreasing
            )
        }));
    }, [matchedEntities, seriesByEntityId, formData.thresholdarray, formData.decreasing]);

    const selectedEntityWithValue = matchedEntitiesWithValues.find(entity => entity.id === selectedEntityId);
    const series = selectedEntityId ? (seriesByEntityId[selectedEntityId] ?? []) : [];

    const emptyReason = !formData.statistic
        ? 'Select a metric to preview matching data.'
        : matchedEntitiesWithValues.length === 0
            ? `No matching ${formData.entitytype === 'host' ? 'hosts' : 'instances'} found.`
            : selectedEntityWithValue && series.length === 0 && !loading && !error
                ? 'No preview data found for the selected entity over the last 24 hours.'
                : null;

    return {
        entityType: formData.entitytype === 'host' ? 'host' : 'datasource',
        matchedEntities: matchedEntitiesWithValues,
        selectedEntityId,
        selectedEntity: selectedEntityWithValue,
        setSelectedEntityId,
        series,
        loading,
        error,
        emptyReason,
        previewFrom: from,
        previewTo: to
    };
};
